// Reachability probe — the only thing allowed to assert "you are offline".
//
// BL-1090. The rule the whole feature turns on: an ANSWERED request proves the
// network path works, whatever the status code. A 500 means the request
// crossed the wire and a server replied — telling a bidder mid-auction that
// they're offline because the server hiccuped is a lie, and an expensive one
// (they stop bidding). Only a fetch that never settles into a response —
// a network-level reject, or a timeout — is evidence of no connectivity.

/** Verdict for a single probe attempt. */
export type ProbeOutcome = 'reachable' | 'unreachable';

/**
 * The path both apps probe (public, unauthenticated, DB-free).
 *
 * **Network-only (BL-2342, ADR 066 — Offline Mode § 5).** The admin phone
 * app's automatic offline mode rests on this request failing when the network
 * is gone. A service worker or any cache that answers it would pin the app
 * "online" with no connection, so every cache layer must pass it straight to the
 * network — BL-2341's service worker consumes this constant for that rule.
 */
export const REACHABILITY_PROBE_PATH = '/api/time';

/** Default ceiling on a single probe before it counts as unreachable. */
export const DEFAULT_PROBE_TIMEOUT_MS = 5_000;

export interface RunReachabilityProbeOptions {
    /** Endpoint to hit. Both apps use their public, DB-free `/api/time`. */
    url: string;
    /** Injected for tests; defaults to the global `fetch`. */
    fetchImpl?: typeof fetch;
    /** Abort ceiling for one attempt. Defaults to {@link DEFAULT_PROBE_TIMEOUT_MS}. */
    timeoutMs?: number;
}

/**
 * Perform one reachability probe.
 *
 * @param options - {@link RunReachabilityProbeOptions}
 * @returns `'reachable'` if any HTTP response came back (including 4xx/5xx),
 *   `'unreachable'` if the request rejected or timed out.
 *
 * @sideEffect Issues one uncached network request to `options.url`.
 */
export async function runReachabilityProbe({
    url,
    fetchImpl,
    timeoutMs = DEFAULT_PROBE_TIMEOUT_MS,
}: RunReachabilityProbeOptions): Promise<ProbeOutcome> {
    const doFetch = fetchImpl ?? globalThis.fetch;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        // `cache: 'no-store'` is load-bearing: a cached 200 would report the
        // network as healthy with the connection fully down.
        await doFetch(url, { cache: 'no-store', signal: controller.signal });
        return 'reachable';
    } catch {
        // Deliberately not inspecting the error: every rejection reaching here
        // (TypeError "Failed to fetch", AbortError, DNS failure) means no
        // response was received, which is the definition of unreachable.
        return 'unreachable';
    } finally {
        clearTimeout(timer);
    }
}
